/**
 * <home-ugc>
 * A scrollable UGC video gallery where ONE video plays at a time.
 * - When the section is on screen the current clip plays; when it ends the
 *   next clip plays and scrolls into view.
 * - Clicking a clip plays it (and stops the others).
 * - Prev/next arrows scroll to and play the previous/next clip.
 */
class HomeUgc extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.home-ugc__viewport');
    this.items = Array.from(this.querySelectorAll('.home-ugc__item'));

    // Sound preference for the whole gallery (autoplay must start muted;
    // once the viewer unmutes, later clips keep sound on).
    this.soundOn = false;

    // Indexes of the items that actually hold a video
    this.videoIndexes = [];
    this.items.forEach((item, i) => {
      const video = item.querySelector('.home-ugc__video');
      if (!video) return;
      this.videoIndexes.push(i);
      video.addEventListener('ended', () => this.advance(i));
      video.addEventListener('click', () => this.activate(i));
      const mute = item.querySelector('[data-ugc-mute]');
      if (mute) {
        mute.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleSound(i);
        });
      }
    });

    this.current = this.videoIndexes.length ? this.videoIndexes[0] : -1;
    this.active = false;

    this.querySelector('[data-ugc-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-ugc-next]')?.addEventListener('click', () => this.step(1));

    // Only run while the gallery is on screen.
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.3) {
            if (!this.active && this.current > -1) this.activate(this.current);
          } else if (this.active) {
            this.active = false;
            this.pauseAll();
          }
        });
      },
      { threshold: [0, 0.3, 1] }
    );
    this.io.observe(this);
  }

  video(i) {
    return this.items[i] ? this.items[i].querySelector('.home-ugc__video') : null;
  }

  pauseAll() {
    this.items.forEach((item) => {
      const v = item.querySelector('.home-ugc__video');
      if (v) v.pause();
      item.removeAttribute('data-playing');
    });
  }

  /** Play the video at item index i, stopping the others, and scroll it in. */
  activate(i) {
    this.active = true;
    this.current = i;
    this.pauseAll();
    this.scrollToItem(i);
    const v = this.video(i);
    if (!v) return;
    v.muted = !this.soundOn;
    this.syncMuteButton(i);
    try {
      v.currentTime = 0;
    } catch (e) {}
    const p = v.play();
    // If autoplay-with-sound is blocked, fall back to muted playback.
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        v.muted = true;
        this.soundOn = false;
        this.syncMuteButton(i);
        const p2 = v.play();
        if (p2 && typeof p2.catch === 'function') p2.catch(() => {});
      });
    }
    this.items[i].setAttribute('data-playing', '');
  }

  /** Toggle sound for the gallery and apply it to the clip at index i. */
  toggleSound(i) {
    this.soundOn = !this.soundOn;
    const v = this.video(i);
    if (v) v.muted = !this.soundOn;
    this.syncMuteButton(i);
  }

  /** Reflect the current muted state on the card's mute button. */
  syncMuteButton(i) {
    const btn = this.items[i] && this.items[i].querySelector('[data-ugc-mute]');
    if (!btn) return;
    btn.setAttribute('aria-pressed', this.soundOn ? 'false' : 'true');
    btn.setAttribute('aria-label', this.soundOn ? 'Mute' : 'Unmute');
  }

  /** When a clip finishes, advance to the next video (wrapping). */
  advance(from) {
    if (this.current !== from || !this.videoIndexes.length) return;
    const pos = this.videoIndexes.indexOf(from);
    const next = this.videoIndexes[(pos + 1) % this.videoIndexes.length];
    this.activate(next);
  }

  /** Prev/next arrow: move to the adjacent video card and play it. */
  step(dir) {
    if (!this.videoIndexes.length) return;
    let pos = this.videoIndexes.indexOf(this.current);
    if (pos < 0) pos = 0;
    pos = Math.min(this.videoIndexes.length - 1, Math.max(0, pos + dir));
    this.activate(this.videoIndexes[pos]);
  }

  scrollToItem(i) {
    const item = this.items[i];
    if (!item || !this.viewport) return;
    const left =
      item.getBoundingClientRect().left -
      this.viewport.getBoundingClientRect().left +
      this.viewport.scrollLeft;
    this.viewport.scrollTo({ left, behavior: 'smooth' });
  }
}

if (!customElements.get('home-ugc')) {
  customElements.define('home-ugc', HomeUgc);
}
